import React, { useState } from 'react'
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline'
import { useObjectUrl } from 'hooks/useObjectUrl'

interface TranscriptUploadFieldProps {
  value: File | string | null
  onChange: (file: File | string | null) => void
  error?: string
  disabled?: boolean
}

const allowedTypes = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
]

const TranscriptViewerFrame = ({ value }: { value: File | string | null }) => {
  const fileUrl = useObjectUrl(value)

  return (
    <iframe
      src={fileUrl ?? ''}
      className="w-full h-full rounded-b-lg"
      title="Transcript Viewer"
    />
  )
}

export const TranscriptUploadField: React.FC<TranscriptUploadFieldProps> = ({
  value,
  onChange,
  error,
  disabled = false
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [showViewer, setShowViewer] = useState(false)

  const handleFileSelect = (file: File) => {
    if (allowedTypes.includes(file.type)) {
      onChange(file)
    } else {
      alert('Please upload a PDF, Word document, or TXT file')
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelect(files[0])
    }
  }

  const removeFile = () => {
    onChange(null)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getFileName = (value: File | string) => {
    if (value instanceof File) {
      return value.name
    }
    // Extract filename from URL
    const urlParts = value.split('/')
    return urlParts[urlParts.length - 1] || 'Transcript file'
  }

  const getFileSize = (value: File | string) => {
    if (value instanceof File) {
      return formatFileSize(value.size)
    }
    return 'Existing file'
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DocumentIcon className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {getFileName(value)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getFileSize(value)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* View Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowViewer(true)
                }}
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                title="View file"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
              {/* Remove Button */}
              <button
                type="button"
                onClick={removeFile}
                disabled={disabled}
                className="text-red-500 hover:text-red-700 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-slate-800'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileInput}
            disabled={disabled}
            className="hidden"
            id="transcript-upload"
          />
          <label
            htmlFor="transcript-upload"
            className={`cursor-pointer ${disabled ? 'cursor-not-allowed' : ''}`}
          >
            <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Drop your transcript file here, or{' '}
                <span className="text-blue-600 dark:text-blue-400 font-medium">
                  browse
                </span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Supports PDF, Word documents (DOC, DOCX), and TXT files
              </p>
            </div>
          </label>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
      )}

      {/* File Viewer Modal */}
      {showViewer && value && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 w-[90%] h-[90%] rounded-lg shadow-lg relative overflow-hidden">
            <button
              className="absolute top-2 right-2 z-[60] flex items-center justify-center w-8 h-8 rounded-full bg-gray-800 text-white hover:bg-gray-700 transition shadow-xl"
              onClick={() => setShowViewer(false)}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            <TranscriptViewerFrame value={value} />
          </div>
        </div>
      )}
    </div>
  )
}
